import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import { motion, AnimatePresence } from "framer-motion";
// import { Link } from "react-router-dom";
import {
  Zap,
  ChevronRight,
  // ArrowLeft,
  CheckCircle2,
  Plus,
  Minus,
  ArrowRight,
  Search,
  // ArrowLeft,
  ShieldPlus,
  AlertCircle,
  UserCheck,
} from "lucide-react";
import Footer from "../../components/Footer";

const dataSecurityFailureCards = [
  {
    title: "ALERT FATIGUE & SECURITY NOISE",
    points: [
      "Critical threats get buried in false positives",
      "Security teams waste time filtering noise",
      "Real attacks hide within normal activity",
    ],
    icon: (
      <AlertCircle className="text-[#34d399]" size={32} strokeWidth={1.5} />
    ),
  },
  {
    title: "CLOUD VISIBILITY GAPS",
    points: [
      "Limited visibility into SaaS and APIs",
      "Fragmented security across hybrid environments",
      "Attackers exploit gaps between tools",
    ],
    icon: <Search className="text-[#34d399]" size={32} strokeWidth={1.5} />,
  },
  {
    title: "MODERN ATTACKS BYPASS DEFENSES",
    points: [
      "Living-off-the-land techniques evade detection",
      "Traditional antivirus and firewalls are bypassed",
      "Attacks blend into normal system behavior",
    ],
    icon: <ShieldPlus className="text-[#34d399]" size={32} strokeWidth={1.5} />,
  },
  {
    title: "SKILLS GAP & SLOW RESPONSE",
    points: [
      "SOC operations require large analyst teams",
      "Many organizations lack continuous monitoring",
      "AI-powered SOC enables automated detection and rapid response",
    ],
    icon: <UserCheck className="text-[#34d399]" size={32} strokeWidth={1.5} />,
  },
];

// --- 2. BEAUTIFIED PROCESS DATA ---
const steps = [
  {
    title: "Integrated Telemetry & Signal Unification",
    desc: "We ingest and correlate telemetry from endpoints, networks, and cloud providers to create a coherent story of activity across your surface.",
    owns: [
      "Integration of existing security stacks",
      "Custom parser development",
      "Health monitoring of log sources",
    ],
  },
  {
    title: "Proactive Threat Hunting",
    desc: "Our threat hunters assume an adversary is already inside, searching for indicators of compromise and subtle behavioral anomalies.",
    owns: [
      "Hypothesis-driven hunting campaigns",
      "Dark web correlation",
      "Detection engineering for zero-days",
    ],
  },
  {
    title: "High-Fidelity Triage & Analysis",
    desc: "We act as the filter, investigating every alert to determine true positives. We only escalate confirmed, enriched incidents.",
    owns: [
      "24/7 alert monitoring",
      "False positive reduction",
      "Eyes-on-glass investigation",
    ],
  },
  {
    title: "Active Containment & Response",
    desc: "When a threat is confirmed, we take action—isolating endpoints and revoking sessions to stop the bleeding immediately.",
    owns: [
      "Host isolation and process termination",
      "Network blocking",
      "Immediate threat neutralization",
    ],
  },
  {
    title: "Root Cause Analysis & Remediation",
    desc: "After the fire is out, we determine how it started—analyzing the vector to provide guidance to prevent recurrence.",
    owns: [
      "Forensic analysis of the incident",
      "Post-mortem reporting",
      "Strategic hardening recommendations",
    ],
  },
  {
    title: "Transparent Reporting & Governance",
    desc: "Real-time visibility into our operations. We deliver executive reports that demonstrate ROI and mean-time-to-respond (MTTR).",
    owns: [
      "Compliance mapping (SOC2/HIPAA/PCI)",
      "Operational metric tracking",
      "Defensible evidence of 24/7 monitoring",
    ],
  },
];

// --- 3. KEY BENEFITS ---
const benefits = [
  {
    title: "24/7/365 Protection",
    desc: "A dedicated team of experts watching your environment nights, weekends, and holidays.",
  },
  {
    title: "Reduced Dwell Time",
    desc: "Detect and contain threats in minutes, not months. Stop attackers before encryption starts.",
  },
  {
    title: "Maximize Tool ROI",
    desc: "We optimize the EDR and security tools you've already paid for, tuning them for lower noise.",
  },
  {
    title: "Eliminate Alert Fatigue",
    desc: "Free your internal IT team from log review, allowing them to focus on high-level strategy.",
  },
  {
    title: "Sophisticated Defense",
    desc: "Gain the capabilities of a Fortune 500 SOC without the massive headcount costs.",
  },
];

// --- 4. FAQ DATA ---
const faqItems = [
  {
    q: "How is MDR different from just having an EDR tool?",
    a: "EDR is the tool; MDR is the engine and the driver. While EDR provides the data, MDR provides the 24/7 human expertise to monitor and respond.",
  },
  {
    q: "Do we need to replace our internal IT team?",
    a: "Not at all. MDR is designed to augment your team. We handle the 24/7 monitoring so your team can focus on strategy.",
  },
  {
    q: "What is your typical Mean Time to Respond (MTTR)?",
    a: "Our goal is to move from detection to containment in minutes using automated playbooks and pre-authorized response actions.",
  },
];

export default function ManagedDetectionAndResponse() {
  const [openFaq, setOpenFaq] = useState(null);

  return (
    <>
      <Helmet>
        <title>Managed Detection & Response (MDR) | Synthera Security</title>

        <meta
          name="description"
          content="24/7 Managed Detection and Response (MDR) services providing threat hunting, incident response, active containment, forensic analysis, and continuous security monitoring."
        />

        <meta
          name="keywords"
          content="Managed Detection and Response, MDR, Threat Hunting, SOC Services, Incident Response, Cybersecurity Monitoring, Threat Detection, Security Operations Center, Synthera Security"
        />

        <meta name="robots" content="index, follow" />

        <meta
          property="og:title"
          content="Managed Detection & Response (MDR) | Synthera Security"
        />

        <meta
          property="og:description"
          content="Stop chasing alerts and start containing threats with 24/7 MDR services from Synthera Security."
        />

        <meta property="og:type" content="website" />

        <meta
          property="og:url"
          content="https://www.syntherasecurity.com/services/detection-and-response"
        />

        <link
          rel="canonical"
          href="https://www.syntherasecurity.com/services/detection-and-response"
        />

        <meta
          property="og:image"
          content="https://res.cloudinary.com/dpn3bkjba/image/upload/f_auto,q_auto:good,w_1200/v1773480312/Managed_detection_and_response_ngutfc.png"
        />
      </Helmet>

      <div className="bg-white min-h-screen font-sans text-slate-900">
        {/* --- HERO SECTION --- */}
        <section className="bg-[#032d28] text-white relative overflow-hidden py-32 px-6">
          {/* Subtle grid background */}
          <div
            className="absolute inset-0 opacity-10 
    bg-[linear-gradient(to_right,#ffffff10_1px,transparent_1px),
    linear-gradient(to_bottom,#ffffff10_1px,transparent_1px)] 
    bg-[size:40px_40px]"
          />

          <div className="relative z-10 max-w-5xl mx-auto text-center">
            {/* Main Title */}
            <h1 className="text-4xl md:text-7xl font-bold leading-[1.05] tracking-tight mb-8">
              Managed Detection & Response
            </h1>

            {/* Divider */}
            <div className="w-20 h-[2px] bg-white/40 mx-auto mb-8" />

            {/* Subtitle */}
            <p className="text-xl md:text-2xl text-white/80 font-medium leading-relaxed max-w-3xl mx-auto">
              Stop chasing alerts — start containing threats.
            </p>
          </div>

          {/* --- FAILURE CARDS (TOP) --- */}
          <div className="container mx-auto max-w-7xl relative z-10 mb-32">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {dataSecurityFailureCards.map((item, index) => (
                <motion.div
                  key={index}
                  whileHover={{ y: -8 }}
                  className="group bg-white p-6 rounded-3xl shadow-lg flex flex-col min-h-[260px] cursor-pointer transition-all hover:shadow-2xl"
                >
                  <div className="mb-4">{item.icon}</div>
                  <h3 className="text-[#34d399] text-base font-bold uppercase tracking-wide mb-4 group-hover:text-[#0c3f38] transition">
                    {item.title}
                  </h3>
                  <div className="flex flex-col w-full">
                    {item.points.map((line, i) => (
                      <div key={i}>
                        <p className="text-[#0c3f38]/70 text-sm font-medium py-1 group-hover:text-[#0c3f38] transition">
                          {line}
                        </p>
                        {i !== item.points.length - 1 && (
                          <div className="h-px w-full bg-[#24ff81]/40 my-[2px]" />
                        )}
                      </div>
                    ))}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* --- SERVICE OVERVIEW --- */}
        <section className="py-32 bg-white">
          <div className="container mx-auto px-6 max-w-6xl">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
              >
                <h2 className="text-[#032d28] text-4xl md:text-5xl font-black uppercase mb-8 leading-tight tracking-tight">
                  Managed Detection & Response
                </h2>
                <div className="space-y-6 text-slate-600 text-lg leading-relaxed font-medium">
                  <p>
                    MDR fails not because tools are missing, but because the
                    human expertise required to interpret them is scarce.
                  </p>
                  <p className="bg-slate-50 p-6 rounded-2xl border-l-4 border-[#24ff81]">
                    Our service focuses on high fidelity signal analysis and
                    active containment moving beyond ticket generation to
                    decisive intervention.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4">
                    {[
                      "24/7 Monitoring",
                      "Active Containment",
                      "Threat Hunting",
                      "Forensic Analysis",
                    ].map((item, i) => (
                      <div
                        key={i}
                        className="flex items-center gap-3 text-[#032d28] font-bold text-base uppercase tracking-tight"
                      >
                        <CheckCircle2 size={20} className="text-[#24ff81]" />{" "}
                        {item}
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
              <div className="relative group">
                <div className="absolute inset-0 bg-[#24ff81]/10 rounded-[48px] rotate-3 group-hover:rotate-0 transition-transform duration-500" />
                <img
                  src="https://res.cloudinary.com/dpn3bkjba/image/upload/f_auto,q_auto:good,w_1200/v1773480312/Managed_detection_and_response_ngutfc.png"
                  className="relative rounded-[48px] shadow-2xl w-full h-[500px] object-cover"
                  alt="Cyber Security Operations Center"
                />
              </div>
            </div>
          </div>
        </section>

        {/* ===== PREMIUM OPERATIONAL CARDS (MDR Forest Style Match) ===== */}
        <section className="relative py-28 bg-[#071c18] overflow-hidden">
          {/* Signature radial glow for the SOC experience */}
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(54,168,138,.15),transparent)]" />

          <div className="container mx-auto px-6 max-w-7xl relative z-10">
            <div className="text-center mb-20">
              <p className="text-emerald-400 uppercase tracking-[0.3em] text-xs font-bold mb-6">
                MDR Security Operations
              </p>
              <h2 className="text-white text-4xl md:text-6xl font-black leading-tight">
                Operational{" "}
                <span className="text-emerald-400">Execution Model</span>
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
              {steps.map((step, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="forest-card rounded-3xl p-8 group cursor-pointer"
                >
                  {/* Icon Container - SOC / Triage Focused */}
                  <div className="icon-container w-14 h-14 rounded-2xl flex items-center justify-center mb-8">
                    <Zap className="text-emerald-400" size={26} />
                  </div>

                  {/* Title */}
                  <h3 className="text-2xl font-semibold text-white mb-4 group-hover:text-emerald-300 transition">
                    {step.title}
                  </h3>

                  {/* Description */}
                  <p className="text-emerald-100/60 text-sm leading-relaxed mb-8">
                    {step.desc}
                  </p>

                  {/* Divider & Service Ownership */}
                  <div className="border-t border-emerald-800/30 pt-6">
                    <p className="text-xs font-semibold text-emerald-500/70 uppercase tracking-widest mb-5">
                      Service Ownership
                    </p>

                    <ul className="space-y-3">
                      {step.owns.map((item, i) => (
                        <li
                          key={i}
                          className="service-item flex items-center text-sm text-emerald-100/80 group-hover:text-white transition-colors"
                        >
                          <ChevronRight
                            className="mr-3 text-emerald-400"
                            size={16}
                          />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Hover Arrow Indicator */}
                  <div className="mt-8 flex justify-end">
                    <ArrowRight
                      className="text-emerald-400 opacity-0 group-hover:opacity-100 -translate-x-4 group-hover:translate-x-0 transition-all duration-300"
                      size={20}
                    />
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
        {/* ================= KEY BENEFITS PREMIUM BALANCED ================= */}
        <section className="py-28 bg-[#071c18] relative overflow-hidden">
          {/* subtle grid */}
          <div
            className="absolute inset-0 opacity-10 
    bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),
    linear-gradient(to_bottom,#80808012_1px,transparent_1px)] 
    bg-[size:40px_40px]"
          />

          {/* heading */}
          <div className="max-w-7xl mx-auto text-center mb-20 px-6 relative z-10">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-white tracking-tight">
              Key Benefits
            </h2>

            <p className="text-emerald-100/60 text-lg max-w-2xl mx-auto">
              Continuous threat detection, rapid containment, and
              enterprise-grade SOC protection without the overhead.
            </p>
          </div>

          <div className="max-w-6xl mx-auto relative z-10">
            {/* ROW 1 */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
              {benefits.slice(0, 3).map((b, i) => (
                <div
                  key={i}
                  className="group relative h-[230px] p-8
          bg-[#031f1c] border border-white/10
          transition-all duration-500
          hover:bg-gradient-to-br hover:from-[#0b3d35] hover:to-[#032d28]
          hover:border-emerald-400/40
          hover:shadow-[0_0_35px_rgba(36,255,129,0.12)]"
                >
                  {/* glow */}
                  <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition duration-500 pointer-events-none">
                    <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(36,255,129,0.18),transparent_60%)]" />
                  </div>

                  <div className="relative z-10">
                    <Zap className="text-emerald-400 mb-5" size={26} />

                    <h3 className="text-xl font-semibold text-white mb-3">
                      {b.title}
                    </h3>

                    <p className="text-white/70 text-sm leading-relaxed">
                      {b.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* ROW 2 centered */}
            <div className="grid grid-cols-1 md:grid-cols-2 mt-0 md:max-w-3xl mx-auto">
              {benefits.slice(3, 5).map((b, i) => (
                <div
                  key={i}
                  className="group relative h-[230px] p-8
          bg-[#031f1c] border border-white/10
          transition-all duration-500
          hover:bg-gradient-to-br hover:from-[#0b3d35] hover:to-[#032d28]
          hover:border-emerald-400/40
          hover:shadow-[0_0_35px_rgba(36,255,129,0.12)]"
                >
                  {/* glow */}
                  <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition duration-500 pointer-events-none">
                    <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(36,255,129,0.18),transparent_60%)]" />
                  </div>

                  <div className="relative z-10">
                    <Zap className="text-emerald-400 mb-5" size={26} />

                    <h3 className="text-xl font-semibold text-white mb-3">
                      {b.title}
                    </h3>

                    <p className="text-white/70 text-sm leading-relaxed">
                      {b.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* --- FAQ SECTION --- */}
        <section className="bg-[#071c18] py-28 px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-[#065c41] text-4xl md:text-6xl font-semibold text-center mb-16 uppercase tracking-tighter">
              MDR FAQ
            </h2>
            <div className="space-y-0 border-t border-white/10">
              {faqItems.map((item, index) => (
                <div key={index} className="border-b border-white/10">
                  <button
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                    className="w-full py-8 flex items-center justify-between text-left group"
                  >
                    <span className="text-white text-lg md:text-xl font-medium group-hover:text-[#24ff81] transition-colors uppercase">
                      {item.q}
                    </span>
                    <div className="ml-4 flex-shrink-0">
                      {openFaq === index ? (
                        <Minus size={28} className="text-[#24ff81]" />
                      ) : (
                        <Plus size={28} className="text-[#24ff81]" />
                      )}
                    </div>
                  </button>
                  <AnimatePresence>
                    {openFaq === index && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="overflow-hidden"
                      >
                        <div className="pb-10 text-white/60 text-lg leading-relaxed font-medium">
                          {item.a}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ))}
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}
